/**
 * Notification → route helper.
 *
 * Maps an in-app notification's event_type + entity_type + entity_id to the
 * URL its click handler should navigate to. Centralized so adding a new
 * event type only touches this file.
 *
 * The notification model (per /api/v1/notifications) currently stores:
 *   - event_type   (e.g. "task_assigned", "rfi_responded")
 *   - entity_type  (e.g. "task", "rfi", "purchase_order")
 *   - entity_id    (the row id of the target entity, may be null)
 *
 * It does NOT store project_id, so deep links to a project-scoped page
 * (e.g. /projects/{project_id}/schedule) aren't possible without an
 * extra API call per click. We land on the global list page instead and
 * append `?peek=<kind>:<id>` so a future peek-from-URL pass can open the
 * detail panel without changing this helper.
 *
 * Unknown event types fall back to `/notifications` — the full list —
 * so the click is never inert.
 */
import type { Notification } from '@/components/organisms/NotificationPanel'

export function notificationRouteFor(notif: Notification): string {
  const evt = (notif.event_type || '').toLowerCase()
  const entity = (notif.entity_type || '').toLowerCase()
  const id = notif.entity_id
  const peek = (kind: string) => (id != null ? `?peek=${kind}:${id}` : '')

  // System / account-level notifications: send to Settings → Notifications.
  if (evt === 'system' || evt === 'account' || evt.startsWith('system_') || evt.startsWith('account_')) {
    return '/settings?tab=Notifications'
  }

  // Task events.
  if (evt.startsWith('task_') || entity === 'task') {
    return `/tasks${peek('task')}`
  }

  // RFI events.
  if (evt.startsWith('rfi_') || entity === 'rfi') {
    return `/rfis${peek('rfi')}`
  }

  // Bid request events.
  if (
    evt.startsWith('bid_') ||
    evt === 'bid_response_received' ||
    evt === 'bid_closing' ||
    entity === 'bid_request'
  ) {
    return `/procurement?tab=bid-requests${id != null ? `&peek=bid-request:${id}` : ''}`
  }

  // Purchase order events.
  if (evt.startsWith('po_') || entity === 'purchase_order') {
    return `/procurement?tab=purchase-orders${id != null ? `&peek=purchase-order:${id}` : ''}`
  }

  // Subcontract events.
  if (evt.startsWith('subcontract_') || entity === 'subcontract') {
    return `/procurement?tab=subcontracts${id != null ? `&peek=subcontract:${id}` : ''}`
  }

  // Bill events.
  if (evt.startsWith('bill_') || entity === 'bill') {
    return `/procurement?tab=bills${id != null ? `&highlight=${id}` : ''}`
  }

  // Change order events.
  if (evt.startsWith('change_order_') || entity === 'change_order') {
    return `/change-orders${peek('co')}`
  }

  // Decision events.
  if (evt.startsWith('decision_') || entity === 'decision') {
    return `/decisions${peek('decision')}`
  }

  // Timesheet events.
  if (evt.startsWith('timesheet_') || entity === 'timesheet') {
    return `/timesheets${peek('ts')}`
  }

  // Invoice events.
  if (evt.startsWith('invoice_') || entity === 'invoice') {
    return `/invoices${peek('invoice')}`
  }

  // Project-level events that don't deep-link to a sub-entity → project page.
  if (evt.startsWith('project_') || entity === 'project') {
    return id != null ? `/projects/${id}` : '/projects'
  }

  // Mention / comment events — without a source_route we land on the full list.
  if (evt === 'mention' || evt.startsWith('comment_') || entity === 'comment') {
    return '/notifications'
  }

  // Daily report.
  if (evt.startsWith('daily_report') || entity === 'daily_log' || entity === 'daily_report') {
    return '/daily-logs'
  }

  // Permit events (PERMITS-REDESIGN-SURFACE-SWEEP).
  // event_types: permit_stage_changed, permit_revoked, permit_issued,
  // permit_conditions_issued. entity_type: 'permit'.
  // All land on the permit detail page; clicking opens the right tab
  // (Timeline is default for stage/revocation events).
  if (evt.startsWith('permit_') || entity === 'permit') {
    return id != null ? `/permits/${id}` : '/permits'
  }

  // Unknown — never leave the click inert.
  return '/notifications'
}
